import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2, Loader2, Monitor, RefreshCw, Smartphone, XCircle } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { DemoQuality, DemoQualityCheck, DemoSite } from '../../../common/hooks/use-demo-sites-api.hook';

/** Label key per check; an id the API adds later falls back to the id itself. */
const CHECK_KEYS: Record<string, string> = {
  page: 'PAGE', visualizer: 'VISUALIZER', gallery: 'GALLERY', overlays: 'OVERLAYS', phone: 'PHONE', assets: 'ASSETS',
  logo: 'LOGO', colors: 'COLORS', language: 'LANGUAGE', rooms: 'ROOMS', areas: 'AREAS',
};

const TONE: Record<string, string> = {
  ready: 'bg-success/10 text-success',
  review: 'bg-warning/10 text-warning',
  broken: 'bg-destructive/10 text-destructive',
  pending: 'bg-muted text-muted-foreground',
  none: 'bg-muted text-muted-foreground',
};

/** The demo's verdict in the list: ready, to review, broken, being checked, or not checked yet. Opens the report. */
export const QualityBadge: React.FC<{ quality?: DemoQuality | null; checking?: boolean; onOpen: () => void }> = ({
  quality, checking, onOpen,
}) => {
  const { t } = useTranslation();
  const status = checking ? 'pending' : quality?.status || 'none';
  const label = t(`SUPER_ADMIN.DEMO_SITES.QUALITY.STATUS_${status.toUpperCase()}`);
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid="quality-badge"
      className={cn('inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium', TONE[status])}
    >
      {status === 'pending' && <Loader2 className="size-3 animate-spin" />}
      {label}
      {typeof quality?.score === 'number' && status !== 'pending' && <span className="opacity-70">{quality.score}</span>}
    </button>
  );
};

const CheckIcon: React.FC<{ status: DemoQualityCheck['status'] }> = ({ status }) =>
  status === 'pass' ? <CheckCircle2 className="size-4 shrink-0 text-success" />
    : status === 'warn' ? <AlertTriangle className="size-4 shrink-0 text-warning" />
      : <XCircle className="size-4 shrink-0 text-destructive" />;

const ORDER = { fail: 0, warn: 1, pass: 2 } as const;

/** The report: what failed first, each with its fix; the desktop and phone screenshots; and a re-check. */
export const QualityReportDialog: React.FC<{
  site: DemoSite | null;
  checking: boolean;
  onRecheck: () => void;
  onClose: () => void;
}> = ({ site, checking, onRecheck, onClose }) => {
  const { t } = useTranslation();
  const quality = site?.quality;
  const checks = [...(quality?.checks || [])].sort((a, b) => ORDER[a.status] - ORDER[b.status]);
  const done = quality && quality.status !== 'pending';

  return (
    <Dialog open={!!site} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {t('SUPER_ADMIN.DEMO_SITES.QUALITY.TITLE', { url: site ? `${site.slug}.ardealy.link` : '' })}
            {site && <QualityBadge quality={quality} checking={checking} onOpen={() => undefined} />}
          </DialogTitle>
          <DialogDescription>
            {checking || quality?.status === 'pending'
              ? t('SUPER_ADMIN.DEMO_SITES.QUALITY.CHECKING')
              : done && quality.checkedAt
                ? t('SUPER_ADMIN.DEMO_SITES.QUALITY.CHECKED_AT', { date: new Date(quality.checkedAt).toLocaleString() })
                : t('SUPER_ADMIN.DEMO_SITES.QUALITY.NEVER_CHECKED')}
          </DialogDescription>
        </DialogHeader>

        {done && (
          <div className="flex max-h-[60vh] flex-col gap-5 overflow-y-auto">
            {(quality.screenshots?.desktop || quality.screenshots?.phone) && (
              <div className="flex gap-3">
                {quality.screenshots?.desktop && (
                  <a href={quality.screenshots.desktop} target="_blank" rel="noreferrer" className="flex-[3] overflow-hidden rounded-lg border">
                    <img src={quality.screenshots.desktop} alt={t('SUPER_ADMIN.DEMO_SITES.QUALITY.DESKTOP')} className="block w-full" />
                    <div className="flex items-center gap-1.5 border-t px-2 py-1 text-xs text-muted-foreground">
                      <Monitor className="size-3.5" />{t('SUPER_ADMIN.DEMO_SITES.QUALITY.DESKTOP')}
                    </div>
                  </a>
                )}
                {quality.screenshots?.phone && (
                  <a href={quality.screenshots.phone} target="_blank" rel="noreferrer" className="flex-1 overflow-hidden rounded-lg border">
                    <img src={quality.screenshots.phone} alt={t('SUPER_ADMIN.DEMO_SITES.QUALITY.PHONE')} className="block w-full" />
                    <div className="flex items-center gap-1.5 border-t px-2 py-1 text-xs text-muted-foreground">
                      <Smartphone className="size-3.5" />{t('SUPER_ADMIN.DEMO_SITES.QUALITY.PHONE')}
                    </div>
                  </a>
                )}
              </div>
            )}
            <ul className="flex flex-col divide-y rounded-lg border" data-testid="quality-checks">
              {checks.map((c) => (
                <li key={c.id} className="flex gap-3 px-3 py-2.5" data-testid={`quality-check-${c.id}`}>
                  <CheckIcon status={c.status} />
                  <div className="flex min-w-0 flex-col gap-0.5">
                    <span className="text-sm font-medium">
                      {CHECK_KEYS[c.id] ? t(`SUPER_ADMIN.DEMO_SITES.QUALITY.CHECK_${CHECK_KEYS[c.id]}`) : c.id}
                    </span>
                    <span className="text-sm text-muted-foreground">{c.message}</span>
                    {c.fix && <span className="text-sm">{t('SUPER_ADMIN.DEMO_SITES.QUALITY.FIX', { fix: c.fix })}</span>}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('SUPER_ADMIN.DEMO_SITES.QUALITY.CLOSE')}</Button>
          <Button onClick={onRecheck} disabled={checking}>
            {checking ? <Loader2 data-icon="inline-start" className="animate-spin" /> : <RefreshCw data-icon="inline-start" />}
            {t('SUPER_ADMIN.DEMO_SITES.QUALITY.RECHECK')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
